import React, { useMemo } from 'react';
import { Button } from 'antd';
import type { TKlarityPayer, TCredentialingProvider } from 'types/credentialing';
import s from './s.module.less';

const DEPOSIT_DIVISOR = 100;

interface Props {
    /** 押金已被 Ops 减免（CREDENTIALING_FEE_WAIVED）：金额划线、实付 $0 */
    feeWaived?: boolean;
    isGroup?: boolean;
    perProviderPayers?: Record<string, string[]>;
    providers?: TCredentialingProvider[];
    selectedPayers: string[];
    payerOptions: TKlarityPayer[];
    onConfirm: () => void;
    onBack?: () => void;
    confirmLoading?: boolean;
}

const ConfirmDepositsStep = ({
    feeWaived,
    isGroup,
    perProviderPayers,
    providers,
    selectedPayers,
    payerOptions,
    onConfirm,
    onBack,
    confirmLoading,
}: Props) => {
    const payerMap = useMemo(() => {
        const map: Record<string, TKlarityPayer> = {};
        payerOptions.forEach((p) => {
            map[String(p.id)] = p;
        });
        return map;
    }, [payerOptions]);

    const resolvePayer = (key: string) => {
        const payer = payerMap[key];
        return {
            key,
            name: payer?.name || key,
            deposit: payer ? payer.depositCents / DEPOSIT_DIVISOR : 50,
        };
    };

    const providerBlocks = useMemo(() => {
        if (!isGroup || !perProviderPayers || !providers) {
            const rows = selectedPayers.map(resolvePayer);
            return [{ providerName: '', payers: rows }];
        }

        return providers
            .filter((prov) => (perProviderPayers[String(prov.providerId)] || []).length > 0)
            .map((prov) => {
                const payers = (perProviderPayers[String(prov.providerId)] || []).map(resolvePayer);
                return { providerName: `${prov.firstName} ${prov.lastName}`, payers };
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGroup, perProviderPayers, providers, selectedPayers, payerMap]);

    const totalPanels = providerBlocks.reduce((n, b) => n + b.payers.length, 0);
    const totalDeposit = providerBlocks.reduce((sum, b) => sum + b.payers.reduce((acc, p) => acc + p.deposit, 0), 0);
    return (
        <>
            <div className={s.modalBody}>
                {providerBlocks.map((block) => (
                    <div key={block.providerName || '_solo'} className={s.providerBlock}>
                        {block.providerName && <div className={s.providerName}>{block.providerName}</div>}
                        <div className={s.payerList}>
                            {block.payers.map((p) => (
                                <div key={p.key} className={s.payerRow}>
                                    <span className={s.payerName}>{p.name}</span>
                                    <span className={`${s.payerFee} ${feeWaived ? s.struck : ''}`}>
                                        ${p.deposit} deposit
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}

                <div className={s.totalRow}>
                    <span>Total deposits</span>
                    <span>
                        <span className={feeWaived ? s.struck : ''}>
                            {totalPanels} panel{totalPanels !== 1 ? 's' : ''} · ${totalDeposit}
                        </span>
                        {feeWaived && <strong className={s.waivedTotal}>$0</strong>}
                    </span>
                </div>
                {feeWaived && <p className={s.waivedNote}>Your credentialing fee is waived by Klarity</p>}
            </div>

            <div className={s.modalFooter}>
                <Button
                    type="primary"
                    block
                    size="large"
                    className={s.primaryBtn}
                    onClick={onConfirm}
                    loading={confirmLoading}
                >
                    Confirm
                </Button>
                {onBack && (
                    <Button block size="large" className={s.backBtn} onClick={onBack}>
                        Back
                    </Button>
                )}
                <p className={s.footerNote}>
                    Your existing credentialing information will be reused. No additional forms required.
                </p>
            </div>
        </>
    );
};

export default ConfirmDepositsStep;
